import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { getNamespace, trackAction } from '@ctct/distui-helpers';

import { FEATURE_UPGRADE_DIALOG_INSTANCES } from '../../constants/upgradeDialogInstances';
import { observabilityAppService } from '../../utils/ObservabilityAppService';
import { withErrorBoundary } from '../ErrorBoundary/ErrorBoundary';

import './IFrameFeatureUpgradeDialog.scss';

const ROSTER_MEMBER_NAME = 'feature-upgrade-dialog';

const IFrameFeatureUpgradeDialog = ({
  instanceId,
  isOpen,
  setIsOpen,
  setCloseDialog,
  stepOneInformation,
  feature,
  activity,
  usage,
  featureRequirements,
  launchPoint,
  paymentSuccessCallback,
  unsavedChangesInCallingPage,
  wrapClassName,
  returnURL,
  returnLabel,
  isFullScreen,
  calledFromDevPage,
  dialogTitle,
  disablePreload,
  promoCode,
}) => {
  const FEATURE_UPGRADE_DIALOG_DISTUI_ID = useMemo(() => {
    if (disablePreload) {
      return ROSTER_MEMBER_NAME;
    }

    // when preloading we need to make sure that dialog has unique id
    const rand = Math.random().toString(36).slice(2, 10);

    return `${ROSTER_MEMBER_NAME}-${rand}`;
  }, [disablePreload]);

  const passedProps = useMemo(
    () => ({
      instanceId,
      isOpen,
      stepOneInformation,
      feature,
      activity,
      usage,
      featureRequirements,
      launchPoint,
      unsavedChangesInCallingPage,
      wrapClassName,
      returnURL,
      returnLabel,
      isFullScreen,
      calledFromDevPage,
      iframeId: FEATURE_UPGRADE_DIALOG_DISTUI_ID,
      promoCode,
    }),
    [
      instanceId,
      isOpen,
      stepOneInformation,
      feature,
      activity,
      usage,
      featureRequirements,
      launchPoint,
      unsavedChangesInCallingPage,
      wrapClassName,
      returnURL,
      returnLabel,
      isFullScreen,
      calledFromDevPage,
      FEATURE_UPGRADE_DIALOG_DISTUI_ID,
      promoCode,
    ]
  );
  const { t } = useTranslation();
  const [distUI, setDistUI] = useState(null);

  // We need to keep state of an upgrade to pass it using setCloseDialog function
  const [successfulUpgrade, setSuccessfulUpgrade] = useState(false);
  const [distUIDialogIsOpen, setDistUIDialogIsOpen] = useState(false);

  // this function will be called when we want to close the overlay, it will close it and then call closeCallbackHandler function.
  // any logic that has to happen on closing dialog has to be added to closeCallbackHandler
  const closeOverlay = useCallback(() => {
    if (distUIDialogIsOpen) {
      distUI?.closeOverlay(FEATURE_UPGRADE_DIALOG_DISTUI_ID);
    }
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, distUI, distUIDialogIsOpen]);

  const trackingText = useMemo(() => {
    const featureInfo = feature || featureRequirements?.map((item) => item.feature).toString();
    const activityInfo = activity || featureRequirements?.map((item) => item.activity).toString();
    const stepAmount = stepOneInformation ? 'two' : 'one';
    const launchPointInfo = launchPoint ? `, launched from ${launchPoint}` : '';

    return `Feature Upgrade Dialog: ${instanceId}, ${stepAmount} step dialog, ${featureInfo}.${activityInfo}${launchPointInfo}`;
  }, [feature, featureRequirements, activity, stepOneInformation, launchPoint, instanceId]);

  const track = useCallback(
    (eventName) => {
      trackAction({ action: `${trackingText}: ${eventName}` });
    },
    [trackingText]
  );

  // this function will be called whenever dist UI dialog is closed
  const closeCallbackHandler = useCallback(() => {
    track('Close');
    setCloseDialog(successfulUpgrade);
    setIsOpen(false);
    // resetting the state of successfulUpgrade in case dialog is re-opened
    setSuccessfulUpgrade(false);
    setDistUIDialogIsOpen(false);
  }, [setCloseDialog, setIsOpen, successfulUpgrade, track]);

  const [child, setChild] = useState(null);
  const [childIframeLoaded, setChildIframeLoaded] = useState(false);
  // we need to keep track if preloading the dialog has started, if preloadId === null, then it has not started, otherwise it should have value of FEATURE_UPGRADE_DIALOG_DISTUI_ID
  const [preloadId, setPreloadId] = useState(null);

  // adding handlers for events dispatched from feature-upgrade-dialog app using distUI
  // if handler is called multiple times, we might need to wrap dispatching event in setTimeout;
  // setTimeout(() => { distUI.dispatchEvent('eventName');}, 0);
  // it seems to be an issue when multiple events are fired one after another in the same function

  const handlers = useMemo(() => {
    return {
      setDialogMaxWidthHandler: ({ maxWidth }) => {
        const container = document.querySelector(
          `.distui.distui-wrapper.overlay_top_${FEATURE_UPGRADE_DIALOG_DISTUI_ID} .dialog-jumbo .modal-content`
        );

        if (container && maxWidth) {
          container.style.maxWidth = maxWidth;
        }
      },
      iFrameLoadedHandler: () => {
        setChildIframeLoaded(true);
      },
      trackEventHandler: ({ action }) => {
        track(action);
      },
      closeModalWindowHandler: closeOverlay,
      setCloseDialogHandler: ({ value }) => {
        setCloseDialog(value);
      },
      paymentSucessHandler: ({ value }) => {
        // save upgrade status locally
        setSuccessfulUpgrade(value);
        paymentSuccessCallback(value);
      },
      closeModalAndRedirectHandler: ({ url }) => {
        setIsOpen(false);

        if (url) {
          window.location.href = url;
        }
      },
      nestedModalVisibilityHandler: ({ visible }) => {
        const dialog = document.querySelector(`.overlay_top_${FEATURE_UPGRADE_DIALOG_DISTUI_ID} .modal-content`);

        const header = document.querySelector(`.overlay_top_${FEATURE_UPGRADE_DIALOG_DISTUI_ID} .dialog-header`);

        if (dialog && header) {
          if (visible) {
            dialog.style.background = '#8a8a8a';
            header.style['border-bottom'] = '1px solid #7a7a7a';
          } else {
            dialog.style.background = '#ffffff';
            header.style['border-bottom'] = '1px solid #e1e1e1';
          }
        }
      },
      updateModalTitleHandler: ({ title, selector }) => {
        const dialogTitle = document.querySelector(selector);

        if (dialogTitle && title) {
          dialogTitle.innerHTML = title;
        }
      },
    };
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, closeOverlay, paymentSuccessCallback, setCloseDialog, setIsOpen, track]);

  const sharedShowOverlayConfig = useMemo(
    () => ({
      dataQeId: 'feature-upgrade-dialog',
      wrapClassName: 'feature-upgrade-dialog-wrapper',
      usage: 'frame',
      title: dialogTitle || t('upgrade-popup.upgrade-your-plan'),
      modal: true,
      kind: isFullScreen ? 'takeover' : 'jumbo',
      height: isFullScreen ? '100%' : 700,
      bootstrapStyle: true,
      resizeWithWindow: true,
      destroyOnClose: false,
      autoResize: '.feature-upgrade-popup',
      closeCallbackHandler,
      hasCloseButton: true,
      buttons: null,
      content: {
        member: {
          rosterMemberName: ROSTER_MEMBER_NAME,
          iframeAttributes: {
            align: 'middle',
            frameBorder: 0,
            marginHeight: 0,
            marginWidth: 0,
            noResize: true,
            scrolling: 'yes',
          },
        },
      },
    }),
    [closeCallbackHandler, dialogTitle, isFullScreen, t]
  );

  useEffect(() => {
    getNamespace().then(({ promise }) => promise().then(setDistUI));
  }, []);

  useEffect(() => {
    return () => {
      setPreloadId(null);
      distUI?.closeOverlay(FEATURE_UPGRADE_DIALOG_DISTUI_ID, null, true);
    };
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, distUI]);

  useEffect(() => {
    const isReady = Boolean(distUI && !isEmpty(handlers) && FEATURE_UPGRADE_DIALOG_DISTUI_ID);

    if (!isReady) {
      return;
    }

    const SET_DIALOG_MAX_WIDTH_EVENT = `setDialogMaxWidth-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const IFRAME_LOADED_EVENT = `iFrameLoaded-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const TRACK_EVENT_EVENT = `trackEvent-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const CLOSE_MODAL_WINDOW_EVENT = `closeModalWindow-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const SET_CLOSE_DIALOG_EVENT = `setCloseDialog-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const PAYMENT_SUCCESS_EVENT = `paymentSucess-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const CLOSE_MODAL_AND_REDIRECT_EVENT = `closeModalAndRedirect-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const NESTED_MODAL_VISIBILITY_EVENT = `nestedModalVisibility-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;
    const UPDATE_MODAL_TITLE_EVENT = `updateModalTitle-${FEATURE_UPGRADE_DIALOG_DISTUI_ID}`;

    distUI.addEventListener({
      type: SET_DIALOG_MAX_WIDTH_EVENT,
      handler: handlers.setDialogMaxWidthHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: IFRAME_LOADED_EVENT,
      handler: handlers.iFrameLoadedHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: TRACK_EVENT_EVENT,
      handler: handlers.trackEventHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: CLOSE_MODAL_WINDOW_EVENT,
      handler: handlers.closeModalWindowHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: SET_CLOSE_DIALOG_EVENT,
      handler: handlers.setCloseDialogHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: PAYMENT_SUCCESS_EVENT,
      handler: handlers.paymentSucessHandler,
      localOnly: false,
    });

    distUI.addEventListener({
      type: CLOSE_MODAL_AND_REDIRECT_EVENT,
      handler: handlers.closeModalAndRedirectHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: NESTED_MODAL_VISIBILITY_EVENT,
      handler: handlers.nestedModalVisibilityHandler,
      localOnly: true,
    });

    distUI.addEventListener({
      type: UPDATE_MODAL_TITLE_EVENT,
      handler: handlers.updateModalTitleHandler,
      localOnly: true,
    });

    // eslint-disable-next-line consistent-return
    return () => {
      distUI.removeEventListener(SET_DIALOG_MAX_WIDTH_EVENT);
      distUI.removeEventListener(IFRAME_LOADED_EVENT);
      distUI.removeEventListener(TRACK_EVENT_EVENT);
      distUI.removeEventListener(CLOSE_MODAL_WINDOW_EVENT);
      distUI.removeEventListener(SET_CLOSE_DIALOG_EVENT);
      distUI.removeEventListener(PAYMENT_SUCCESS_EVENT);
      distUI.removeEventListener(CLOSE_MODAL_AND_REDIRECT_EVENT);
      distUI.removeEventListener(NESTED_MODAL_VISIBILITY_EVENT);
      distUI.removeEventListener(UPDATE_MODAL_TITLE_EVENT);
    };
  }, [distUI, handlers, FEATURE_UPGRADE_DIALOG_DISTUI_ID]);

  const preloadDialog = useCallback(() => {
    if (!disablePreload && !childIframeLoaded && preloadId !== FEATURE_UPGRADE_DIALOG_DISTUI_ID) {
      distUI?.showOverlay({
        ...sharedShowOverlayConfig,
        id: FEATURE_UPGRADE_DIALOG_DISTUI_ID,
        refresh: false,
        isOpen: false,
      });

      setPreloadId(FEATURE_UPGRADE_DIALOG_DISTUI_ID);
    }

    return () => {
      setPreloadId(null);
    };
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, preloadId, disablePreload, childIframeLoaded, distUI, sharedShowOverlayConfig]);

  // setting connection with child iFrame when pre-loading the dialog
  useEffect(() => {
    if (disablePreload || !childIframeLoaded || child) {
      return;
    }

    const childiFrame = document?.getElementById(`${FEATURE_UPGRADE_DIALOG_DISTUI_ID}_iframe`);

    getNamespace().then((namespace) => {
      const connection = namespace.ipc?.connectToChild({
        iframe: childiFrame,
        transportId: 'feature-upgrade-dialog-connection',
      });

      connection.promise.then((child) => {
        setChild(child);
      });
    });
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, child, childIframeLoaded, disablePreload]);

  const showPreloadedDialog = useCallback(() => {
    if (!distUI) {
      return;
    }

    distUI.showOverlay({
      ...sharedShowOverlayConfig,
      id: FEATURE_UPGRADE_DIALOG_DISTUI_ID,
      refresh: false,
      isOpen: true,
    });
  }, [FEATURE_UPGRADE_DIALOG_DISTUI_ID, distUI, sharedShowOverlayConfig]);

  const showDialog = useCallback(() => {
    const stringifedpassedProps = JSON.stringify(passedProps);

    distUI?.showOverlay({
      ...sharedShowOverlayConfig,
      id: ROSTER_MEMBER_NAME,
      refresh: false,
      isOpen: true,
      destroyOnClose: true,
      content: {
        ...sharedShowOverlayConfig.content,
        member: {
          ...sharedShowOverlayConfig.content.member,
          urlParameters: { props: stringifedpassedProps },
        },
      },
    });
  }, [distUI, passedProps, sharedShowOverlayConfig]);

  useEffect(() => {
    preloadDialog();
  }, [preloadDialog]);

  useEffect(() => {
    if (isOpen) {
      if (disablePreload) {
        showDialog();
      } else if (childIframeLoaded) {
        showPreloadedDialog();
      }

      track('Open');
      setDistUIDialogIsOpen(true);
    }
  }, [disablePreload, childIframeLoaded, isOpen, showDialog, showPreloadedDialog, track]);

  // sync props with child iFrame when pre-loading the dialog
  useEffect(() => {
    if (!disablePreload) {
      child?.passProps(passedProps);
    }
  }, [child, disablePreload, passedProps]);

  return null;
};

IFrameFeatureUpgradeDialog.defaultProps = {
  stepOneInformation: null,
  usage: null,
  launchPoint: null,
  featureRequirements: null,
  paymentSuccessCallback: () => {},
  unsavedChangesInCallingPage: false,
  isFullScreen: false,
  wrapClassName: null,
  setCloseDialog: () => {},
  setIsOpen: () => {},
  returnURL: null,
  returnLabel: null,
  calledFromDevPage: false,
  dialogTitle: null,
  promoCode: null,
};

IFrameFeatureUpgradeDialog.propTypes = {
  instanceId: PropTypes.oneOf(Object.values(FEATURE_UPGRADE_DIALOG_INSTANCES)),
  isOpen: PropTypes.bool.isRequired,
  // optional:
  setIsOpen: PropTypes.func,
  // optional:
  // Similar to "setIsOpen" but more accurately named and will send a boolean param to indicate if upgrade succeeded or not.
  // Useful for caller to refresh page and entitlements upon dialog close.
  setCloseDialog: PropTypes.func,
  feature: PropTypes.string.isRequired,
  activity: PropTypes.string.isRequired,
  usage: PropTypes.number,
  // featureRequirements array is optional
  featureRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      feature: PropTypes.string.isRequired,
      activity: PropTypes.string.isRequired,
      usage: PropTypes.number,
    })
  ),
  launchPoint: PropTypes.string,
  unsavedChangesInCallingPage: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  paymentSuccessCallback: PropTypes.func,
  // optional, used to return from the first time pay flow
  returnURL: PropTypes.string,
  returnLabel: PropTypes.string,
  stepOneInformation: PropTypes.shape({
    featureName: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    subHeader: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    // optional:
    featureList: PropTypes.arrayOf(PropTypes.string),
  }),
  wrapClassName: PropTypes.string,
  calledFromDevPage: PropTypes.bool,
  dialogTitle: PropTypes.string,
  disablePreload: PropTypes.bool,
  promoCode: PropTypes.string,
};

export default memo(withErrorBoundary(IFrameFeatureUpgradeDialog, observabilityAppService));
